import { type MentionItem, type MentionSource } from '@/features/mention';
import { queryClient } from '@/query/client';

import { getVfsCatalogueQuery, type Skill, toSkills } from './plugins-query';
import { skillIcon } from './skill-icon';

const SKILL_KIND = 'skill';
const BUILT_IN_SECTION_TITLE = 'Built-in skills (internal only)';
const TEAM_SECTION_TITLE = 'Team skills';

const skillToMention = (skill: Skill): MentionItem => ({
  id: skill.dir,
  kind: SKILL_KIND,
  name: skill.name,
  label: skill.label,
  detail: skill.description,
  data: { isSystem: skill.isSystem },
  section: skill.isSystem
    ? BUILT_IN_SECTION_TITLE
    : skill.isTeam
      ? TEAM_SECTION_TITLE
      : undefined,
});

/**
 * `/` picker over the skills catalogue. A pick becomes plain `/name` text,
 * which is both what the sent message shows and the only signal the agent gets.
 * The agent's prompt lists skills by that same directory name.
 */
export const createSkillSource = ({
  onSelect,
}: {
  onSelect?: (mention: MentionItem) => void;
} = {}): MentionSource => ({
  kind: SKILL_KIND,
  // Section heading for the user's own skills; built-in ones carry their own
  title: 'Your skills',
  listItems: async () => {
    const entries = await queryClient.fetchQuery(getVfsCatalogueQuery());
    // Broken skills are dead ends here; the plugins page is where they get fixed
    const skills = toSkills(entries).filter(
      (skill) => !skill.isBroken && (!skill.isTeam || skill.teamSkill?.enabled)
    );
    return [
      ...skills.filter((skill) => !skill.isSystem && !skill.isTeam),
      ...skills.filter((skill) => skill.isTeam),
      ...skills.filter((skill) => skill.isSystem),
    ].map(skillToMention);
  },
  renderIcon: (mention) => {
    const Icon = skillIcon({
      name: mention.name,
      description: mention.detail ?? '',
      isSystem: mention.data?.isSystem === true,
    });
    return <Icon className="size-4 shrink-0" strokeWidth={1.5} />;
  },
  onSelect,
});
